import { Eyebrow, THEME_PREFERENCES, token, useTheme, type ThemePreference } from '@bp-files/ui';
import { SurfaceHeader } from '@/surfaces/SurfaceHeader';
import { GENERAL_COPY } from './general.copy';

/**
 * General — the first settings panel that belongs to the reader rather than to
 * the agency (**ADR-0064**).
 *
 * It carries one control today, and the shape is chosen for the second: a page
 * of sections, each opened by an `<Eyebrow as="h2">`, rather than a page that is
 * a theme switcher. "How this app looks on this device" is the whole scope, and
 * everything device-local lands here as it arrives.
 *
 * **Not a surface**, on the same grounds as Passkeys and Signature: `SURFACES`
 * holds the five things the product is *about* (SPEC §§4, 6, 8–10), all five of
 * which are agency work. It is in the Settings sidebar because that is where a
 * person goes looking, which is a different question from what the registry is
 * for — `settingsGroups()` in `surfaces.ts` is where the two meet.
 */
export function GeneralSettings() {
  return (
    <SurfaceHeader title={GENERAL_COPY.title} summary={GENERAL_COPY.summary}>
      <section className="flex flex-col gap-3">
        <Eyebrow as="h2">{GENERAL_COPY.theme.heading}</Eyebrow>
        <ThemeSwitcher />
      </section>
    </SurfaceHeader>
  );
}

/**
 * Light / Dark / System, as a segmented control.
 *
 * **Three radios in a `<fieldset>`, styled as segments — not three buttons.**
 * This is one exclusive choice among a small fixed set, which is what a radio
 * group *is*: arrow keys move between the options, the legend names the group,
 * and the browser tells a screen reader "2 of 3" without anybody writing
 * `aria-checked`. Three buttons would look identical and would have to
 * re-implement all of that, worse.
 *
 * **The selected segment is `bp-blue-deep` with `bp-on-accent` on it**, which is
 * this product's fill for a chosen thing and — the reason it is not a lighter
 * surface token — the only pair that stays legible in both palettes. A segment
 * filled with `bp-card` would be white on white in daylight, where `bp-input`,
 * `bp-panel`, and `bp-card` are all `#ffffff`.
 *
 * Local to this file rather than a `packages/ui` composition: it has one caller,
 * and a shared component with one caller is an abstraction bought before it is
 * needed — `AccountMenu` and `SessionGate` in `App.tsx` set the precedent.
 * Promote it when a second setting wants a segmented control.
 */
function ThemeSwitcher() {
  const { preference, setPreference } = useTheme();

  return (
    <fieldset className="flex flex-col gap-2 border-0 p-0">
      <legend className="pb-2 text-sm font-medium text-body">{GENERAL_COPY.theme.label}</legend>

      <div
        className="inline-flex w-fit gap-1 border p-1"
        style={{
          background: token('bp-input'),
          borderColor: token('bp-border-strong'),
          borderRadius: token('radius-full'),
        }}
      >
        {THEME_PREFERENCES.map((option) => (
          <ThemeOption
            key={option}
            option={option}
            selected={preference === option}
            onSelect={setPreference}
          />
        ))}
      </div>

      <p className="text-xs" style={{ color: token('bp-text-muted') }}>
        {GENERAL_COPY.theme.hint}
      </p>
    </fieldset>
  );
}

const OPTION_LABEL: Record<ThemePreference, string> = {
  system: GENERAL_COPY.theme.system,
  light: GENERAL_COPY.theme.light,
  dark: GENERAL_COPY.theme.dark,
};

/**
 * One segment.
 *
 * The `<input>` is visually hidden rather than `display: none` — a hidden input
 * is removed from the accessibility tree and from the tab order, which would
 * leave a control no keyboard can reach. `sr-only` keeps it focusable, and the
 * `<label>` around it is what carries the appearance, including the focus ring
 * via `focus-within`.
 */
function ThemeOption({
  option,
  selected,
  onSelect,
}: {
  option: ThemePreference;
  selected: boolean;
  onSelect: (preference: ThemePreference) => void;
}) {
  return (
    <label
      className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm font-medium transition-colors focus-within:outline focus-within:outline-2 focus-within:outline-offset-2"
      style={{
        background: selected ? token('bp-blue-deep') : 'transparent',
        color: selected ? token('bp-on-accent') : token('bp-text-muted'),
        borderRadius: token('radius-full'),
        outlineColor: token('bp-blue-rail'),
      }}
    >
      <input
        type="radio"
        name="theme"
        value={option}
        checked={selected}
        onChange={() => {
          onSelect(option);
        }}
        className="sr-only"
      />
      <ThemeIcon option={option} />
      {OPTION_LABEL[option]}
    </label>
  );
}

/**
 * Monitor, sun, and moon — Lucide's `monitor`, `sun`, and `moon`, hand-copied.
 * `apps/web` has no `lucide-react` dependency and `App.tsx`'s `BackIcon` and
 * `AccountIcon` set the precedent. 16px rather than the usual 20: these sit
 * inside a label beside its text, not alone inside a control.
 */
function ThemeIcon({ option }: { option: ThemePreference }) {
  const common = {
    viewBox: '0 0 24 24',
    width: 16,
    height: 16,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  } as const;

  if (option === 'system') {
    return (
      <svg {...common}>
        <rect width="20" height="14" x="2" y="3" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    );
  }

  if (option === 'light') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}
